import React from 'react'
import { TaskService } from '@duck/api-definitions'
import { IconTooltip, MarkdownEditor } from '@duck/ui'
import { TranslationKey, useTar } from '@/modules'

interface TaskFormFieldProps {
  label?: TranslationKey
  field: keyof TaskService.Task
  type?: 'text' | 'textarea' | 'number' | 'select' | 'markdown'
  required?: boolean
  options?: readonly { value: string; label: TranslationKey }[]
  disabled?: boolean
  placeholderOverride?: string
  errorOverride?: string
  tooltip?: string
  formData: Partial<TaskService.Task>
  onChange: (field: keyof TaskService.Task, value: string | number | string[] | undefined) => void
  fieldErrors?: Record<string, string>
}

export const TaskFormField: React.FC<TaskFormFieldProps> = ({
  label,
  field,
  type = 'text',
  required = false,
  options,
  disabled = false,
  placeholderOverride,
  errorOverride,
  tooltip,
  formData,
  onChange,
  fieldErrors = {},
}) => {
  const { t } = useTar()
  const value = formData[field] ?? ''
  const placeholder =
    placeholderOverride ??
    t('common.enterValue', { value: label ? t(label).toLowerCase() : "" })
  const baseInputClasses = `
    w-full px-3 py-2 text-sm border border-border rounded-md
    bg-card text-foreground placeholder-secondary/60
    focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
    transition-colors
    ${disabled ? 'bg-secondary/5 text-muted-foreground cursor-not-allowed' : 'hover:border-secondary'}
  `

  return (
    <div className="space-y-2" data-testid={`task-field-wrapper-${field}`}>
      {label && (
        <label className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          {t(label)}
          {required && <span className="text-error">*</span>}
          {tooltip && <IconTooltip content={tooltip} />}
        </label>
      )}
      {type === 'markdown' ? (
        <MarkdownEditor
          defaultValue={value as string}
          onChange={(content) => onChange(field, content)}
          placeholder={placeholder}
          className="max-h-[420px]"
          mdClassName="bg-background"
          mode={disabled ? 'view' : 'edit'}
        />
      ) : type === 'textarea' ? (
        <textarea
          data-testid={`task-field-${field}`}
          value={value as string}
          onChange={(e) => onChange(field, e.target.value)}
          disabled={disabled}
          className={`${baseInputClasses} resize-vertical`}
          rows={4}
          placeholder={placeholder}
          autoComplete="off"
        />
      ) : type === 'select' && options ? (
        <select
          data-testid={`task-field-${field}`}
          value={value as string}
          onChange={(e) => onChange(field, e.target.value)}
          disabled={disabled}
          className={baseInputClasses}
          autoComplete="off"
        >
          <option value="">
            {t('common.select')} {label ? t(label).toLowerCase() : ""}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
      ) : type === 'number' ? (
        <input
          data-testid={`task-field-${field}`}
          type="number"
          value={value as number}
          onChange={(e) => { const n = parseFloat(e.target.value); onChange(field, isNaN(n) ? undefined : n) }}
          disabled={disabled}
          className={baseInputClasses}
          placeholder={placeholder}
          step="0.01"
          autoComplete="off"
        />
      ) : (
        <input
          data-testid={`task-field-${field}`}
          type="text"
          value={value as string}
          onChange={(e) => onChange(field, e.target.value)}
          disabled={disabled}
          className={baseInputClasses}
          placeholder={placeholder}
          autoComplete="off"
        />
      )}
      {(errorOverride ?? fieldErrors[field]) && (
        <p className="text-sm text-error mt-1">
          {errorOverride ?? fieldErrors[field]}
        </p>
      )}
    </div>
  )
}
